import { useQuery, useSuspenseQuery } from '@tanstack/react-query';

import { getRequest } from '@/api/rest/hooks/requests/get';
import {
  GET_PLOT_LABEL_NAMES_PATH,
  GET_PLOT_LABEL_VALUES_PATH,
  GetPlotLabelNamesErrorDto,
  GetPlotLabelNamesSuccessDto,
  GetPlotLabelValuesErrorDto,
  GetPlotLabelValuesSuccessDto,
} from '@/api/rest/resources/types/organization';
import { useOrganizationId } from '@/pages/shared/hooks/useOrganizationId';
import { assertIsDefined } from '@/utils/assert';

type SuccessLabelNamesDto = GetPlotLabelNamesSuccessDto;
type ErrorLabelNamesDto = GetPlotLabelNamesErrorDto;

export const usePlotLabelNames = (organizationIdArg?: string, limit?: number, offset?: number) => {
  const orgId = useOrganizationId();
  const organizationId = organizationIdArg ?? orgId;

  assertIsDefined(organizationId, 'organizationId');

  const queryParams = { organization_id: organizationId, limit, offset };

  const queryFn = async () => {
    return getRequest<SuccessLabelNamesDto>(GET_PLOT_LABEL_NAMES_PATH, undefined, queryParams);
  };

  return useSuspenseQuery<SuccessLabelNamesDto, ErrorLabelNamesDto>({
    queryKey: ['plot-label-names', queryParams],
    queryFn,
    staleTime: 1000 * 60 * 5,
  });
};

type SuccessLabelValuesDto = GetPlotLabelValuesSuccessDto;
type ErrorLabelValuesDto = GetPlotLabelValuesErrorDto;

export const usePlotLabelNameValues = (plotLabelNameId: string | null, limit?: number, offset?: number) => {
  const queryParams = { limit, offset };
  const pathVariables = { plot_label_name_id: plotLabelNameId };

  const queryFn = async () => {
    return getRequest<SuccessLabelValuesDto>(GET_PLOT_LABEL_VALUES_PATH, pathVariables, queryParams);
  };

  return useQuery<SuccessLabelValuesDto, ErrorLabelValuesDto>({
    queryKey: ['plot-label-names', 'values', pathVariables, queryParams],
    queryFn,
    staleTime: 1000 * 60 * 5,
    enabled: !!plotLabelNameId,
  });
};
